import { useCallback, useEffect, useState } from "react";

import { getWaStatusAccount } from "../../../../utils/broadcast";

export const WA_ACCOUNT_STATUS_PHASE = Object.freeze({
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
});

// The endpoint can answer 200 with an empty body. Only an object is a status.
const isStatusBody = (body) =>
  body !== null && typeof body === "object" && !Array.isArray(body);

/**
 * Status of the WhatsApp account behind the campaign's channel: its daily
 * messaging limit and its quality rating.
 *
 * The status always belongs to the channel of the campaign, looked up by id
 * in the channel list, never to the first channel of the list. The phase is
 * derived on every render from the request it belongs to, so a change of
 * channel shows `loading` in that same render and never the previous
 * channel's status.
 *
 * @param {Object} params
 * @param {string} params.channelId - channel of the campaign being created
 * @param {Array} params.channels - WhatsApp channels of the session
 * @param {boolean} params.channelsLoaded - whether `channels` is final. While
 *   it is not, a channel missing from the list is still loading, not an error.
 * @returns {{ phase: string, accountStatus: Object|null, channel: Object|null, canRetry: boolean, retry: Function }}
 *   `canRetry` is true exactly when `retry()` would start a request: there is
 *   a channel with a provider id to ask for.
 */
export const useWaAccountStatus = ({ channelId, channels, channelsLoaded }) => {
  const channel =
    (channelId &&
      Array.isArray(channels) &&
      channels.find((entry) => entry?.id === channelId)) ||
    null;
  const providerId = channel?.providerId || null;
  // What the status is asked for. It only says which channel, not which
  // request: the same channel can be asked for more than once.
  const targetKey = providerId ? `${channel.id}|${providerId}` : null;

  // Number of the request that is current now. It advances on every change
  // of target and on every retry, so coming back to a channel seen before is
  // a new request and never matches the outcome kept from the earlier one.
  const [request, setRequest] = useState({ targetKey, id: 0 });
  let requestId = request.id;
  if (request.targetKey !== targetKey) {
    // State adjusted while rendering: React runs this render again with the
    // new state before committing anything. The new number is already used
    // below, so not even this first pass reports the previous status.
    requestId += 1;
    setRequest({ targetKey, id: requestId });
  }

  // Outcome of the last request that was still current when it settled.
  const [settled, setSettled] = useState(null);

  useEffect(() => {
    if (!providerId) return undefined;

    // Turned off when the channel changes, on retry and on unmount: an answer
    // that arrives after that belongs to a request nobody is waiting for.
    let isCurrent = true;
    getWaStatusAccount(providerId)
      .then((body) => {
        if (!isCurrent) return;
        setSettled(
          isStatusBody(body)
            ? { requestId, phase: WA_ACCOUNT_STATUS_PHASE.READY, accountStatus: body }
            : { requestId, phase: WA_ACCOUNT_STATUS_PHASE.ERROR, accountStatus: null }
        );
      })
      .catch(() => {
        // getWaStatusAccount already reported the failure, timeout included.
        if (!isCurrent) return;
        setSettled({ requestId, phase: WA_ACCOUNT_STATUS_PHASE.ERROR, accountStatus: null });
      });

    return () => {
      isCurrent = false;
    };
  }, [requestId, providerId]);

  const retry = useCallback(() => {
    if (providerId) setRequest((state) => ({ ...state, id: state.id + 1 }));
  }, [providerId]);

  if (providerId) {
    const hasSettled = settled?.requestId === requestId;
    return {
      phase: hasSettled ? settled.phase : WA_ACCOUNT_STATUS_PHASE.LOADING,
      accountStatus: hasSettled ? settled.accountStatus : null,
      channel,
      canRetry: true,
      retry,
    };
  }

  // No request can be made. It is an error once nothing else can arrive: the
  // channel is there without a provider id, or the final list does not have it.
  const isUnresolvable = Boolean(channel) || Boolean(channelId && channelsLoaded);
  return {
    phase: isUnresolvable
      ? WA_ACCOUNT_STATUS_PHASE.ERROR
      : WA_ACCOUNT_STATUS_PHASE.LOADING,
    accountStatus: null,
    channel,
    canRetry: false,
    retry,
  };
};
